import moment from 'moment';
import _ from 'lodash';
import { LitEntity, SirenActionMixin, html, css, renderIf } from '../../utility/lit-entity';
import { LevelColoursMixin } from '../../utility/level-colours-mixin';
import { sharedStyles } from '../../styles/shared-styles';
import { bannerStyles } from '../../styles/banner-styles';
import './device-monitoring-chart-layer-areas';
import { colour as colourLevels } from './device-monitoring-chart-layer-levels';
import { colour as colourBattery } from './device-monitoring-chart-layer-battery';
import { colour as colourCell } from './device-monitoring-chart-layer-cell';
import { colour as colourBatteryTemperature } from './device-monitoring-chart-layer-battery-temperature';
import { colour as colourSensorTemperature } from './device-monitoring-chart-layer-sensor-temperature';
import './device-monitoring-chart-layer-warning-tabs';
import './device-monitoring-chart-data';
import './device-monitoring-chart-bin-profile';
import './device-monitoring-chart-header-details';
import '../../common/bn-icon';

const MAX_WEEKS_SHOWN = 4;
const DATE_FORMAT_STRING = 'MMM D YYYY, h:mm a';

export class BinMonitoringChart extends LevelColoursMixin(SirenActionMixin(LitEntity)) {
  constructor() {
    super();

    this.headerTitle = '-';
    this.headerSub = '-';
    const now = new Date();
    this.start = moment(now).utc().subtract(1, 'week').format();
    this.end = moment(now).utc().format();
  }

  static get properties() {
    return {
      hide: { type: Boolean },
      start: { type: String },
      end: { type: String },
      selectable: { type: Boolean },
      dateHovering: { type: String, attribute: 'date-hovering', reflect: true },
      hardwareType: { type: String, attribute: 'hardware-type' },
      visibleLayers: { type: Array, attribute: 'visible-layers', reflect: true },
      headerDetailsVisible: { type: Boolean, attribute: false },
      headerTitle: { type: String, attribute: false },
      headerSub: { type: String, attribute: false },
      levels: { type: Array, attribute: false },
      events: { type: Array, attribute: false },
      totalVolume: { type: Number, attribute: false },
      hoverNoReportSeconds: { type: Number, attribute: false },
      selectedDate: { type: String, attribute: false },
      prevDate: { type: String, attribute: false },
      nextDate: { type: String, attribute: false },
      defaultWeeks: { type: Number, attribute: 'default-weeks' },
      loadStartDate: { type: String, attribute: 'load-start-date' },
    };
  }

  _getLayerNames() {
    return ['levels', 'battery', 'cell', 'batteryTemperature', 'sensorTemperature'];
  }

  _initializeDateRange() {
    const weeks = this.defaultWeeks || 1;
    const startDate = this.loadStartDate ? new Date(this.loadStartDate) : new Date();

    this.start = moment(startDate).utc().subtract(weeks, 'week').format();
    this.end = moment(startDate).utc().format();
  }

  updated(changedProperties) {
    // Handle date range initialization from parameters before first render
    if (changedProperties.has('defaultWeeks') || changedProperties.has('loadStartDate')) {
      this._initializeDateRange();
    }

    if (!changedProperties.has('selectedDate') && !changedProperties.has('selectable')) {
      return;
    }

    if (!this.selectedDate || !this.selectable) {
      const layers = this.shadowRoot.querySelectorAll('.chart-layers *');
      layers.forEach((layer) => layer.setSelectedDate(undefined));
      return;
    }

    const selectedDateIndex = _.findIndex(this.events, { rawDate: this.selectedDate });
    this.nextDate = this.events[selectedDateIndex - 1]?.rawDate;
    this.prevDate = this.events[selectedDateIndex + 1]?.rawDate;

    const layers = this.shadowRoot.querySelectorAll('.chart-layers *');
    layers.forEach((layer) => layer.setSelectedDate(moment(this.selectedDate).valueOf()));
    this.dispatchEvent(new CustomEvent('select', { detail: { date: this.selectedDate }, bubbles: true, composed: true }));
  }

  static get styles() {
    return [sharedStyles, bannerStyles, css`
      :host {
        display: flex;
        flex-direction: column;
        margin: .5em 0 0 0;
        --left-gutter-size: 40px;
        --right-gutter-size: 70px;
        width: 100%;
        font-size: .9em;
      }
      .chart-layers {
        position: relative;
        padding: .5em 0;
        width: 100%;
        height: 100%;
        box-sizing: border-box;
      }
      .chart-layers * {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
      }
      .row {
        display: flex;
        align-content: stretch;
        flex-direction: row;
        gap: .5em;
      }
      bn-loading {
        width: 100%;
        height: 100%;
      }
      .no-data-banner {
        margin: 10px auto;
        display: flex;
        align-items: center;
        width: 50%;
        min-width: 300px;
        justify-content: center;
      }
      .header-container {
        flex-direction: row;
        margin: 0 calc(var(--right-gutter-size) + .25em) 0 calc(var(--left-gutter-size));
        justify-content: space-between;
        align-items: flex-end;
      }
      .content {
        height: 152px;
      }
      .profile-column {
        background-color: var(--surface);
        border: 2px solid var(--gray-70);
        border-radius: 4px;
        padding: 0 .25em .25em .25em;
        width: var(--right-gutter-size);
      }
      .paging-column {
        display: flex;
        flex-direction: column-reverse;
      }
      .paging-button {
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        font-weight: bold;
        font-size: 1.25em;
        padding: 0;
        width: var(--left-gutter-size);
        border: 2px solid rgba(0, 0, 0, 0.5);
      }
      .paging-button.prev {
        flex-grow: 5;
        margin-bottom: -2px;
      }
      .paging-button.next {
        font-size: 1.125em;
        flex-grow: 1;
      }
      .footer {
        margin: 0 calc(var(--right-gutter-size) + .5rem) 0 calc(var(--left-gutter-size) + .5rem);
        place-content: space-between;
        align-items: center;
      }
      .footer .check-boxes {
        display: flex;
      }
      .footer label {
        text-transform: capitalize;
        display: flex;
        padding: .25rem;
        margin: .25rem .25rem 0 0;
        border-radius: 2px;
        cursor: pointer;
        transition: .2s background-color;
        user-select: none;
        font-size: 12px;
        align-items: center;
      }
      .footer label input {
        cursor: pointer;
        margin-left: 0;
        border: 1px solid var(--white-base);
      }
      .footer label.levels {
        background-color: ${colourLevels};
        color: var(--white-base);
      }
      .footer label.battery {
        background-color: ${colourBattery};
        color: var(--white-base);
      }
      .footer label.cell {
        background-color: ${colourCell};
        color: var(--white-base);
      }
      .footer label.batteryTemperature {
        background-color: ${colourBatteryTemperature};
        color: var(--black-base);
      }
      .footer label.sensorTemperature {
        background-color: ${colourSensorTemperature};
        color: var(--white-base);
      }
      .footer label:not(.checked) {
        background-color: transparent;
        color: var(--black-base);
      }
    `];
  }

  _disableNextButton({ startMoment, nowMoment }) {
    return nowMoment.diff(startMoment, 'days') <= 7;
  }

  render() {
    const startMoment = moment(this.start);
    const nowMoment = moment(new Date());
    return html`
      ${this._renderHeader()}

      <div class="row content">
        <div class="paging-column">
          <button
            class="paging-button next button button--standard"
            @click="${this._nextWeek}"
            ?disabled=${this._disableNextButton({ startMoment, nowMoment })}
          >
            <bn-icon name="far fa-chevron-right"></bn-icon>
          </button>
          <button class="paging-button prev button button--standard" @click="${this._prevWeek}">
            <bn-icon name="far fa-chevron-left"></bn-icon>
          </button>
        </div>

        ${this._renderChartLayers()}

        <div class="profile-column">
          <device-monitoring-chart-bin-profile
            .href="${this.binHref}"
            .level="${this.hoveredLevel}"
          ></device-monitoring-chart-bin-profile>
        </div>

        <device-monitoring-chart-data
          start="${this.start}"
          end="${this.end}"
          .href="${this.href}"
          @chart_data="${this._onChartData}"
        ></device-monitoring-chart-data>
      </div>

      ${this._renderFooter({ startMoment, nowMoment })}
    `;
  }

  _renderFooter({ startMoment }) {
    const endMoment = moment(this.end);

    return html`
      <div class="row footer">
        <div class="left-date">
          ${startMoment.format(DATE_FORMAT_STRING)}
        </div>
        <div class="check-boxes">
          ${this._renderCheckBoxes()}
        </div>
        <div class="right-date">
          ${endMoment.format(DATE_FORMAT_STRING)}
        </div>
      </div>
      ${renderIf(this.selectable, () => html`
        <div class="row">
          <button class="button button--plain" ?disabled="${!this.prevDate}" @click="${this._clickPrev}">
            Previous Level
          </button>
          <button class="button button--plain" ?disabled="${!this.nextDate}" @click="${this._clickNext}">
            Next Level
          </button>
        </div>
      `)}
    `;
  }

  _clickNext() {
    this.selectedDate = this.nextDate;
  }

  _clickPrev() {
    this.selectedDate = this.prevDate;
  }

  _renderCheckBoxes() {
    return this._getLayerNames().map((layerName) => {
      const checked = (this.visibleLayers || []).indexOf(layerName) > -1;
      return html`
        <label class="${layerName} ${checked ? 'checked' : ''}">
          <input
            type="checkbox"
            ?checked=${checked}
            @input="${() => this._onCheckboxInput(layerName)}"
          >
          <span title="label">${layerName}</span>
        </label>
      `;
    });
  }

  _renderChartLayers() {
    if (this.totalVolumes === undefined || this.totalVolumes.length === 0 || !this.start || !this.end || !this.levels || !this.events) {
      return html`<bn-loading></bn-loading>`;
    }

    if (this.events.length === 0 && this.levels.length === 0) {
      return html`
        <div class="no-data-banner banner banner--info">
          No data found for this time range
        </div>
      `;
    }

    const totalVolumesJSON = JSON.stringify(this.totalVolumes);
    const toggleableLayers = {
      levels: () => html`
        <device-monitoring-chart-layer-levels
          start="${this.start}"
          end="${this.end}"
          total-volumes="${totalVolumesJSON}"
          .levels="${this.levels}"
          .events="${this.events}"
          .hide="${this.hide}"
          hardware-type="${this.hardwareType}"
        ></device-monitoring-chart-layer-levels>
      `,
      battery: () => html`
        <device-monitoring-chart-layer-battery
          start="${this.start}"
          end="${this.end}"
          total-volumes="${totalVolumesJSON}"
          .levels="${this.levels}"
          .events="${this.events}"
          .hide="${this.hide}"
          hardware-type="${this.hardwareType}"
        ></device-monitoring-chart-layer-battery>
      `,
      cell: () => html`
        <device-monitoring-chart-layer-cell
          start="${this.start}"
          end="${this.end}"
          total-volumes="${totalVolumesJSON}"
          .levels="${this.levels}"
          .events="${this.events}"
          .hide="${this.hide}"
          hardware-type="${this.hardwareType}"
        ></device-monitoring-chart-layer-cell>
      `,
      batteryTemperature: () => html`
        <device-monitoring-chart-layer-battery-temperature
          start="${this.start}"
          end="${this.end}"
          total-volumes="${totalVolumesJSON}"
          .levels="${this.levels}"
          .events="${this.events}"
          .hide="${this.hide}"
          hardware-type="${this.hardwareType}"
        ></device-monitoring-chart-layer-battery-temperature>
      `,
      sensorTemperature: () => html`
        <device-monitoring-chart-layer-sensor-temperature
          start="${this.start}"
          end="${this.end}"
          total-volumes="${totalVolumesJSON}"
          .levels="${this.levels}"
          .events="${this.events}"
          .hide="${this.hide}"
          hardware-type="${this.hardwareType}"
        ></device-monitoring-chart-layer-sensor-temperature>
      `,
    };

    const toggleableLayersHtml = this._getLayerNames().map((layerName) => {
      const index = (this.visibleLayers || []).indexOf(layerName);
      return index >= 0 ? toggleableLayers[layerName]() : undefined;
    });

    return html`
      <div class="chart-layers">
        <device-monitoring-chart-layer-areas
          start="${this.start}"
          end="${this.end}"
          total-volumes="${totalVolumesJSON}"
          .levels="${this.levels}"
          .events="${this.events}"
          .hide="${this.hide}"
          .selectedDate="${this.selectedDate}"
          @hoverarea="${this._onHoverArea}"
          @hoverevent="${this._onHoverEvent}"
          @clickevent="${this._onClickEvent}"
          hardware-type="${this.hardwareType}"
        ></device-monitoring-chart-layer-areas>
        <device-monitoring-chart-layer-warning-tabs
          start="${this.start}"
          end="${this.end}"
          total-volumes="${totalVolumesJSON}"
          .levels="${this.levels}"
          .events="${this.events}"
          .hide="${this.hide}"
          hardware-type="${this.hardwareType}"
        ></device-monitoring-chart-layer-warning-tabs>
        ${toggleableLayersHtml}
      </div>
    `;
  }

  _renderHeader() {
    return html`
      <div class="row header-container">
        <device-monitoring-chart-header-details
          .levels="${this.levels}"
          .events="${this.events}"
          .visibleLayers="${this.visibleLayers}"
          date-hovering="${this.dateHovering}"
          no-report-seconds="${this.hoverNoReportSeconds}"
        ></device-monitoring-chart-header-details>
      </div>
    `;
  }

  _onChartData(e) {
    const { levels, events, totalVolumes } = e.detail;
    this.levels = levels;
    this.events = events;
    this.totalVolumes = totalVolumes;

    if (!this.binHref && levels.length > 0 && levels[0].binId) {
      this.binHref = levels[0].binId;
      [this.hoveredLevel] = levels;
    }

    if (!this.selectedDate && this.events?.length > 0) {
      this.selectedDate = this.events[0].rawDate;
    }
  }

  _onHoverEvent(e) {
    const { date } = e.detail || {};

    this.hoverNoReportSeconds = date ? undefined : this.hoverNoReportSeconds;
    this.dateHovering = date;

    const layers = this.shadowRoot.querySelectorAll('.chart-layers *');
    layers.forEach((layer) => layer.setHoveredDate(moment(this.dateHovering).valueOf()));

    const level = this.levels.find((level) => level.rawDate === this.dateHovering);
    if (level && level.binId) {
      this.binHref = level.binId;
      this.hoveredLevel = level;
    }
  }

  _onClickEvent(e) {
    const { date } = e.detail || {};
    if (!this.selectable) {
      return;
    }

    this.selectedDate = date;
  }

  _onCheckboxInput(layerName) {
    const index = (this.visibleLayers || []).indexOf(layerName);
    const layersDup = (this.visibleLayers || []).map((layer) => layer);
    if (index === -1) {
      layersDup.push(layerName);
    } else {
      layersDup.splice(index, 1);
    }

    this.visibleLayers = layersDup;
  }

  _onHoverArea(e) {
    const { area } = e.detail;
    if (!area) {
      this.hoverNoReportSeconds = undefined;
      return;
    }

    this.hoverNoReportSeconds = area.secondsDiff;
    const selectableLayers = this.shadowRoot.querySelectorAll('.chart-layers *');
    selectableLayers.forEach((layer) => layer.setSelectedDate(''));
  }

  _prevWeek() {
    this.start = moment(this.start).utc().subtract(1, 'week').format();
    if (moment(this.end).diff(moment(this.start), 'weeks') > MAX_WEEKS_SHOWN) {
      this.end = moment(this.end).utc().subtract(1, 'week').format();
    }
  }

  _nextWeek() {
    const startMoment = moment(this.start);
    const endMoment = moment(this.end);
    const endDiff = moment(new Date()).diff(endMoment, 'days');
    const startDiff = moment(new Date()).diff(startMoment, 'days');

    if (endDiff <= 1) {
      if (startDiff > 7) {
        this.start = startMoment.utc().add(1, 'week').format();
      }

      return;
    }

    this.start = startMoment.utc().add(1, 'week').format();
    this.end = endMoment.utc().add(1, 'week').format();
  }
}

window.customElements.define('device-monitoring-chart', BinMonitoringChart);
